<template>
  <div class="work">
    <div class="work-header-card">
      <div class="work-header-row">
        <h3>合同管理</h3>
        <div class="work-header-actions">
          <el-input
            v-model="form2Query.key"
            placeholder="搜索合同（项目名称/建设单位/服务内容）"
            class="work-search-input"
            clearable
            @keyup.enter.native="onQuery"
          ></el-input>
          <el-button type="primary" size="mini" @click="onQuery">筛选</el-button>
          <el-button size="mini" @click="resetQuery">重置</el-button>
          <el-button
            type="success"
            size="mini"
            @click="onExportXLS"
            :loading="exportLoading"
          >
            导出全部
          </el-button>
        </div>
      </div>
    </div>

    <!-- 显示列选择 -->
    <div class="check-group">
      <span>显示列：</span>
      <el-checkbox-group v-model="visibleColumns" size="small">
        <el-checkbox v-for="col in visibleColumnOptions" :label="col.value" :key="col.value" style="margin-right:10px">
          {{ col.name }}
        </el-checkbox>
      </el-checkbox-group>
      <el-button type="text" size="mini" @click="toggleAllColumns" style="margin-left: 10px;">
        {{ isAllColumnsVisible ? '隐藏全部' : '显示全部' }}
      </el-button>
    </div>

    <!-- 合同表格 -->
    <div class="table-container">
      <el-table 
        :data="tableData" 
        border 
        style="width: 100%" 
        @sort-change="onSortChange" 
        :max-height="tableHeight"
        v-loading="loading" 
        :key="tableKey"
        class="contract-table">
        <!-- 动态列渲染 -->
        <template v-for="col in computedColumns">
          <el-table-column 
            v-if="col.ifShow" 
            :prop="col.value" 
            :width="col.width || undefined" 
            :min-width="col.minWidth || (col.width ? undefined : getMinWidth(col.value))"
            :label="col.name"
            :sortable="col.value !== 'warningStatus' && col.value !== 'applyStatus'" 
            :key="col.value"
            :show-overflow-tooltip="col.value === 'WorkName' || col.value === 'content' || col.value === 'build_company' || col.value === 'win_company'"
            header-align="center"
            :align="col.align || 'center'"
            :resizable="true">
            <template slot-scope="scope">
              <!-- 申请状态列 -->
              <span v-if="col.value === 'applyStatus'">
                <el-tag :type="getApplyStatusTagType(scope.row)" size="small"
                  :style="{ fontSize: isMobile ? '12px' : '13px', padding: isMobile ? '4px 6px' : '6px 8px' }">
                  {{ getApplyStatusText(scope.row) }}
                </el-tag>
              </span>
              <!-- 预警状态列（特殊样式） -->
              <span v-if="col.value === 'warningStatus'">
                <el-tag :type="scope.row.warningColor" size="small"
                  :style="{ fontSize: isMobile ? '12px' : '13px', padding: isMobile ? '4px 6px' : '6px 8px' }">
                  {{ scope.row.warningText }}
                </el-tag>
              </span>

              <!-- 附件列 -->
              <span v-else-if="col.value === 'attachment_name'">
                <span v-if="scope.row.attachment_name"
                  :style="{ color: '#409EFF', fontSize: isMobile ? '12px' : '14px', cursor: 'pointer' }"
                  @click="downloadAttachment(scope.row)">
                  📄 {{ formatAttachmentName(scope.row.attachment_name) }}
                </span>
                <span v-else :style="{ color: '#C0C4CC', fontSize: isMobile ? '12px' : '14px' }">
                  无附件
                </span>
              </span>

              <!-- 剩余天数列 -->
              <span v-else-if="col.value === 'remainingDays'">
                <span v-if="scope.row.remainingDays < 0"
                  :style="{ color: '#67C23A', fontSize: isMobile ? '12px' : '14px' }">
                  已完成
                </span>
                <span v-else-if="scope.row.remainingDays <= 5"
                  :style="{ color: '#F56C6C', fontWeight: 'bold', fontSize: isMobile ? '12px' : '14px' }">
                  {{ scope.row.remainingDays }}天
                </span>
                <span v-else-if="scope.row.remainingDays <= 10"
                  :style="{ color: '#E6A23C', fontWeight: 'bold', fontSize: isMobile ? '12px' : '14px' }">
                  {{ scope.row.remainingDays }}天
                </span>
                <span v-else :style="{ color: '#909399', fontSize: isMobile ? '12px' : '14px' }">
                  {{ scope.row.remainingDays }}天
                </span>
              </span>

              <!-- 价格字段 -->
              <span v-else-if="['FirstPrice', 'SecondPrice', 'PingPrice', 'PayPrice', 'HtPrice'].includes(col.value)">
                {{ formatPrice(scope.row[col.value]) }}
              </span>

              <!-- MinusPrice 特殊处理 -->
              <span v-else-if="col.value === 'MinusPrice'">
                {{ (scope.row['FirstPrice'] != null && scope.row['SecondPrice'] != null) ?
                  (scope.row['MinusPrice'] ? '¥' + Number(scope.row['MinusPrice']).toFixed(2) : '¥0.00') :
                  '-' }}
              </span>

              <!-- 备注列 - 长文本截断 -->
              <span v-else-if="col.value === 'note' && scope.row[col.value]">
                <span v-if="scope.row[col.value].length > (isMobile ? 10 : 20)">
                  <el-tooltip :content="scope.row[col.value]" placement="top">
                    <span>{{ scope.row[col.value].substring(0, isMobile ? 10 : 20) }}...</span>
                  </el-tooltip>
                </span>
                <span v-else>
                  {{ scope.row[col.value] }}
                </span>
              </span>

              <!-- DeadLine 只显示年月日 -->
              <span v-else-if="col.value === 'DeadLine'">
                <div :style="{ fontSize: isMobile ? '12px' : '14px' }">
                  {{ formatDateForDisplay(scope.row[col.value]) }}
                </div>
                <div v-if="isMobile && scope.row.warningColor === 'danger'"
                  :style="{ color: '#F56C6C', fontSize: '10px', marginTop: '2px' }">
                  即将到期
                </div>
              </span>

              <!-- 项目名称 - 移除硬编码截断，让Element UI自动处理溢出 -->
              <span v-else-if="col.value === 'WorkName'" :style="{ fontSize: isMobile ? '12px' : '14px' }">
                {{ scope.row[col.value] }}
              </span>

              <!-- 其他字段 -->
              <span v-else :style="{ fontSize: isMobile ? '12px' : '14px' }">
                {{ formatCellContent(scope.row[col.value], col.value) }}
              </span>
            </template>
          </el-table-column>
        </template>


        <!-- 操作列 -->
        <el-table-column 
          label="操作" 
          :width="getActionColumnWidth()" 
          :min-width="getActionColumnWidth()"
          fixed="right" 
          align="center"
          class-name="action-column">
          <template slot-scope="scope">
            <div class="action-buttons">
              <!-- 员工/部门经理：查看按钮 -->
              <el-tooltip content="查看" placement="top" v-if="roleId != 1">
                <el-button type="text" size="mini" @click="handleView(scope.row)" icon="el-icon-view"
                  :style="{ color: '#909399', marginRight: isMobile ? '4px' : '8px' }">
                  <span v-if="isMobile">查看</span>
                </el-button>
              </el-tooltip>
              <!-- 员工：申请修改按钮 -->
              <el-tooltip content="申请修改" placement="top" v-if="roleId != 1">
                <el-button type="text" size="mini" @click="handleApplyModify(scope.row)" icon="el-icon-edit-outline"
                  :style="{ color: '#409EFF', marginRight: isMobile ? '4px' : '8px' }">
                  <span v-if="isMobile">申请</span>
                </el-button>
              </el-tooltip>
              <!-- 编辑按钮（只有管理员 roleId=1 能看到） -->
              <el-tooltip content="编辑" placement="top" v-if="roleId == 1">
                <el-button type="text" size="mini" @click="handleUpdate(scope.$index, scope.row)" icon="el-icon-edit"
                  :style="{ color: '#409EFF', marginRight: isMobile ? '4px' : '8px' }">
                  <span v-if="isMobile">编辑</span>
                </el-button>
              </el-tooltip>

              <!-- 抄送人：管理员、部门经理 -->
              <el-tooltip content="抄送人" placement="top" v-if="canManageCc">
                <el-button type="text" size="mini" @click="openCcDialog(scope.row)" icon="el-icon-message"
                  :style="{ color: '#909399', marginRight: isMobile ? '4px' : '8px' }">
                  <span v-if="isMobile">抄送</span>
                </el-button>
              </el-tooltip>

              <!-- 附件按钮（只有管理员 roleId=1 能看到） -->
              <el-tooltip :content="scope.row.attachment_name ? '下载附件' : '上传附件'" placement="top" v-if="roleId == 1">
                <el-button type="text" size="mini" @click="handleAttachment(scope.row)"
                  :icon="scope.row.attachment_name ? 'el-icon-download' : 'el-icon-upload2'" :style="{
                    color: scope.row.attachment_name ? '#67C23A' : '#E6A23C',
                    marginRight: isMobile ? '4px' : '8px'
                  }">
                  <span v-if="isMobile">{{ scope.row.attachment_name ? '下载' : '上传' }}</span>
                </el-button>
              </el-tooltip>

              <!-- 确认完成按钮（所有人都能看到，但只在未完成状态显示） -->
              <el-tooltip content="确认完成" placement="top" v-if="!scope.row.isCompleted">
                <el-button type="text" size="mini" @click="confirmComplete(scope.row)" icon="el-icon-check"
                  :style="{ color: '#67C23A', marginRight: isMobile ? '4px' : '8px' }">
                  <span v-if="isMobile">完成</span>
                </el-button>
              </el-tooltip>

              <!-- 撤销完成按钮（所有人都能看到，但只在已完成状态显示） -->
              <el-tooltip content="撤销完成" placement="top" v-if="scope.row.isCompleted">
                <el-button type="text" size="mini" @click="undoComplete(scope.row)" icon="el-icon-refresh-left"
                  :style="{ color: '#909399', marginRight: isMobile ? '4px' : '8px' }">
                  <span v-if="isMobile">撤销</span>
                </el-button>
              </el-tooltip>

              <!-- 删除按钮（只有管理员 roleId=1 能看到） -->
              <el-popconfirm title="确定删除吗？" @confirm="handleDel(scope.$index, scope.row)" confirm-button-text="确定"
                cancel-button-text="取消" v-if="roleId == 1">
                <el-tooltip slot="reference" content="删除" placement="top">
                  <el-button type="text" size="mini" icon="el-icon-delete" :style="{ color: '#F56C6C' }">
                    <span v-if="isMobile">删除</span>
                  </el-button>
                </el-tooltip>
              </el-popconfirm>
            </div>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 分页 -->
    <el-pagination class="pagination" @size-change="handleSizeChange" @current-change="handleCurrentChange"
      :current-page="currentPage" :page-sizes="isMobile ? [5, 10, 15] : [10, 20, 30, 40, 50, 100]" :page-size="pageSize"
      :layout="paginationLayout" :small="ifSmall" :total="dataCount" :style="{ marginTop: '15px' }">
    </el-pagination>


    <!-- 编辑合同对话框 -->
    <el-dialog title="编辑合同" :visible.sync="ifShowUpdateDialog" :width="getDialogWidth()" :fullscreen="isMobile"
      top="5vh" @closed="handleDialogClosed">
      <div class="dialog-content">
        <div class="dialog-header">
          <span class="contract-name">{{ selecthetong.WorkName }}</span>
        </div>

        <el-form label-position="top" ref="updateForm" :model="newForm" :rules="formRules">

          <!-- 基础信息 - 自适应布局 -->
          <div class="form-section">
            <div class="section-title">基础信息</div>
            <el-row :gutter="getFormGutter()">
              <el-col :xs="24" :sm="24" :md="12" :lg="8" :xl="8">
                <el-form-item label="项目名称" prop="WorkName" required>
                  <el-input placeholder="请输入项目名称" v-model="newForm.WorkName" size="small" class="form-item"></el-input>
                </el-form-item>
              </el-col>
              <el-col :xs="24" :sm="24" :md="12" :lg="8" :xl="8">
                <el-form-item label="归档编号/报告号" prop="ReturnId">
                  <el-input placeholder="请输入归档编号/报告号" v-model="newForm.ReturnId" size="small"
                    class="form-item"></el-input>
                </el-form-item>
              </el-col>
              <el-col :xs="24" :sm="24" :md="12" :lg="8" :xl="8">
                <el-form-item label="截止时间" prop="DeadLine" required>
                  <el-date-picker type="date" placeholder="请选择截止日期" v-model="newForm.DeadLine" style="width: 100%"
                    format="yyyy-MM-dd" value-format="yyyy-MM-dd" size="small" class="form-item"></el-date-picker>
                </el-form-item>
              </el-col>
            </el-row>
          </div>

          <!-- 公司信息 - 自适应布局 -->
          <div class="form-section">
            <div class="section-title">公司信息</div>
            <el-row :gutter="getFormGutter()">
              <el-col :xs="24" :sm="12" :md="12" :lg="8" :xl="8">
                <el-form-item label="建设单位" prop="build_company" required>
                  <el-input placeholder="请输入建设单位" v-model="newForm.build_company" size="small"
                    class="form-item"></el-input>
                </el-form-item>
              </el-col>
              <el-col :xs="24" :sm="12" :md="12" :lg="8" :xl="8">
                <el-form-item label="承接单位" prop="receive_companyId" required>
                  <el-select v-model="newForm.receive_companyId" placeholder="请选择承接单位" style="width: 100%"
                    :loading="loadingSubjects" filterable default-first-option size="small" class="form-item">
                    <el-option v-for="subject in subjectList" :key="subject.id" :label="subject.SubjectName"
                      :value="subject.id">
                    </el-option>
                  </el-select>
                </el-form-item>
              </el-col>
              <el-col :xs="24" :sm="24" :md="12" :lg="8" :xl="8">
                <el-form-item label="中标/送审单位" prop="win_company">
                  <el-input placeholder="请输入中标/送审单位" v-model="newForm.win_company" size="small"
                    class="form-item"></el-input>
                </el-form-item>
              </el-col>
            </el-row>
          </div>

          <!-- 服务内容 -->
          <div class="form-section">
            <div class="section-title">服务内容</div>
            <el-form-item prop="content">
              <el-select v-model="newForm.content" placeholder="请选择服务内容" style="width: 100%"
                :loading="loadingServiceContents" filterable size="small" class="form-item">
                <el-option v-for="item in serviceContentList" :key="item.id" :label="item.ContentName"
                  :value="item.ContentName">
                </el-option>
              </el-select>
            </el-form-item>
          </div>

          <!-- 价格信息 - 自适应布局 -->
          <div class="form-section">
            <div class="section-title">价格信息</div>
            <!-- 第一行价格 -->
            <el-row :gutter="getFormGutter()">
              <el-col :xs="24" :sm="12" :md="12" :lg="6" :xl="6">
                <el-form-item label="招标控制价/审定价">
                  <el-input type="number" placeholder="请输入金额" v-model.number="newForm.FirstPrice" step="0.01"
                    size="small" class="form-item price-input">
                    <template slot="append">元</template>
                  </el-input>
                </el-form-item>
              </el-col>
              <el-col :xs="24" :sm="12" :md="12" :lg="6" :xl="6">
                <el-form-item label="中标/送审价">
                  <el-input type="number" placeholder="请输入金额" v-model.number="newForm.SecondPrice" step="0.01"
                    size="small" class="form-item price-input">
                    <template slot="append">元</template>
                  </el-input>
                </el-form-item>
              </el-col>
              <el-col :xs="24" :sm="12" :md="12" :lg="6" :xl="6">
                <el-form-item label="合同金额">
                  <el-input type="number" placeholder="请输入金额" v-model.number="newForm.HtPrice" step="0.01" size="small"
                    class="form-item price-input">
                    <template slot="append">元</template>
                  </el-input>
                </el-form-item>
              </el-col>
              <el-col :xs="24" :sm="12" :md="12" :lg="6" :xl="6">
                <el-form-item label="已开票金额">
                  <el-input type="number" placeholder="请输入金额" v-model.number="newForm.PayPrice" step="0.01" size="small"
                    class="form-item price-input">
                    <template slot="append">元</template>
                  </el-input>
                </el-form-item>
              </el-col>
            </el-row>
            <!-- 第二行价格 -->
            <el-row :gutter="getFormGutter()" style="margin-top: 10px;">
              <el-col :xs="24" :sm="12" :md="12" :lg="12" :xl="12">
                <el-form-item label="评委费">
                  <el-input type="number" placeholder="请输入金额" v-model.number="newForm.PingPrice" step="0.01"
                    size="small" class="form-item price-input">
                    <template slot="append">元</template>
                  </el-input>
                </el-form-item>
              </el-col>
              <el-col :xs="24" :sm="12" :md="12" :lg="12" :xl="12">
                <el-form-item label="核减额">
                  <el-input
                    :value="(newForm.FirstPrice && newForm.SecondPrice) ? (newForm.SecondPrice - newForm.FirstPrice).toFixed(2) : ''"
                    placeholder="自动计算" disabled size="small" class="form-item">
                    <template slot="append">元</template>
                  </el-input>
                </el-form-item>
              </el-col>
            </el-row>
          </div>

          <!-- 备注和附件 -->
          <div class="form-section">
            <div class="section-title">其他信息</div>
            <el-row :gutter="getFormGutter()">
              <el-col :xs="24" :sm="24" :md="24" :lg="12" :xl="12">
                <el-form-item label="备注">
                  <el-input type="textarea" placeholder="请输入备注" v-model="newForm.note" :rows="isMobile ? 3 : 4"
                    size="small" class="form-item" resize="none" maxlength="2000" show-word-limit></el-input>
                </el-form-item>
              </el-col>
              <el-col :xs="24" :sm="24" :md="24" :lg="12" :xl="12">
                <el-form-item label="抄送人">
                  <cc-user-select
                    v-model="newForm.ccUserIds"
                    :options="ccCandidates"
                    :loading="ccLoading"
                    placeholder="按姓名 / 工号 / 部门搜索"
                  />
                </el-form-item>
              </el-col>
              <el-col :xs="24" :sm="24" :md="24" :lg="12" :xl="12">
                <el-form-item label="合同附件">
                  <div class="attachment-section">
                    <!-- 现有附件 -->
                    <div v-if="currentAttachment" class="current-attachment">
                      <div class="attachment-info">
                        <span class="attachment-icon">📄</span>
                        <span class="attachment-name" :title="currentAttachment.name">
                          {{ formatAttachmentName(currentAttachment.name) }}
                        </span>
                        <el-popconfirm title="确定删除这个附件吗？" @confirm="deleteCurrentAttachment">
                          <el-button slot="reference" size="mini" type="text" class="delete-btn">删除</el-button>
                        </el-popconfirm>
                      </div>
                    </div>

                    <!-- 上传区域 -->
                    <div class="upload-area">
                      <el-upload class="upload-demo" ref="updateUpload" action="#" :auto-upload="false"
                        :on-change="handleUpdateAttachmentChange" :on-remove="handleUpdateAttachmentRemove"
                        :before-upload="beforeUpdateAttachmentUpload" :file-list="updateAttachmentFiles" :limit="1"
                        accept=".pdf" :show-file-list="false">
                        <el-button size="small" type="primary" icon="el-icon-upload">
                          {{ currentAttachment ? '更换附件' : '上传附件' }}
                        </el-button>
                        <div class="upload-tip">
                          只能上传PDF文件，且不超过30MB
                        </div>
                      </el-upload>
                    </div>
                  </div>
                </el-form-item>
              </el-col>
            </el-row>
          </div>

        </el-form>

        <div class="dialog-footer">
          <el-button @click="ifShowUpdateDialog = false" size="small">取消</el-button>
          <el-button type="primary" @click="confirmUpdate" size="small" :loading="updating">提交修改</el-button>
        </div>
      </div>
    </el-dialog>

    <!-- 查看合同对话框（只读，供员工/部门经理查看完整数据） -->
    <el-dialog title="查看合同" :visible.sync="ifShowViewDialog" :width="getDialogWidth()" :fullscreen="isMobile"
      top="5vh" class="view-dialog">
      <div class="view-dialog-content" v-if="viewRow">
        <div class="view-dialog-header">
          <span class="contract-name">{{ viewRow.WorkName }}</span>
        </div>
        <!-- 基础信息 -->
        <div class="view-section">
          <div class="view-section-title">基础信息</div>
          <el-row :gutter="getFormGutter()">
            <el-col :xs="24" :sm="24" :md="12" :lg="8">
              <div class="view-item">
                <span class="view-label">项目名称</span>
                <span class="view-value">{{ viewRow.WorkName || '-' }}</span>
              </div>
            </el-col>
            <el-col :xs="24" :sm="24" :md="12" :lg="8">
              <div class="view-item">
                <span class="view-label">归档编号/报告号</span>
                <span class="view-value">{{ viewRow.ReturnId || '-' }}</span>
              </div>
            </el-col>
            <el-col :xs="24" :sm="24" :md="12" :lg="8">
              <div class="view-item">
                <span class="view-label">截止时间</span>
                <span class="view-value">{{ formatDateForDisplay(viewRow.DeadLine) }}</span>
              </div>
            </el-col>
          </el-row>
        </div>
        <!-- 公司信息 -->
        <div class="view-section">
          <div class="view-section-title">公司信息</div>
          <el-row :gutter="getFormGutter()">
            <el-col :xs="24" :sm="12" :md="12" :lg="8">
              <div class="view-item">
                <span class="view-label">建设单位</span>
                <span class="view-value">{{ viewRow.build_company || '-' }}</span>
              </div>
            </el-col>
            <el-col :xs="24" :sm="12" :md="12" :lg="8">
              <div class="view-item">
                <span class="view-label">承接单位</span>
                <span class="view-value">{{ viewRow.SubjectName || '-' }}</span>
              </div>
            </el-col>
            <el-col :xs="24" :sm="24" :md="12" :lg="8">
              <div class="view-item">
                <span class="view-label">中标/送审单位</span>
                <span class="view-value">{{ viewRow.win_company || '-' }}</span>
              </div>
            </el-col>
          </el-row>
        </div>
        <!-- 服务内容 -->
        <div class="view-section">
          <div class="view-section-title">服务内容</div>
          <div class="view-item view-item-block">
            <span class="view-value view-value-block">{{ viewRow.content || '-' }}</span>
          </div>
        </div>
        <!-- 价格信息 -->
        <div class="view-section">
          <div class="view-section-title">价格信息</div>
          <el-row :gutter="getFormGutter()">
            <el-col :xs="24" :sm="12" :md="12" :lg="6">
              <div class="view-item">
                <span class="view-label">招标控制价/审定价</span>
                <span class="view-value">{{ formatPrice(viewRow.FirstPrice) }}</span>
              </div>
            </el-col>
            <el-col :xs="24" :sm="12" :md="12" :lg="6">
              <div class="view-item">
                <span class="view-label">中标/送审价</span>
                <span class="view-value">{{ formatPrice(viewRow.SecondPrice) }}</span>
              </div>
            </el-col>
            <el-col :xs="24" :sm="12" :md="12" :lg="6">
              <div class="view-item">
                <span class="view-label">合同金额</span>
                <span class="view-value">{{ formatPrice(viewRow.HtPrice) }}</span>
              </div>
            </el-col>
            <el-col :xs="24" :sm="12" :md="12" :lg="6">
              <div class="view-item">
                <span class="view-label">已开票金额</span>
                <span class="view-value">{{ formatPrice(viewRow.PayPrice) }}</span>
              </div>
            </el-col>
            <el-col :xs="24" :sm="12" :md="12" :lg="12">
              <div class="view-item">
                <span class="view-label">评委费</span>
                <span class="view-value">{{ formatPrice(viewRow.PingPrice) }}</span>
              </div>
            </el-col>
            <el-col :xs="24" :sm="12" :md="12" :lg="12">
              <div class="view-item">
                <span class="view-label">核减额</span>
                <span class="view-value">{{ (viewRow.FirstPrice != null && viewRow.SecondPrice != null) ? formatPrice(viewRow.SecondPrice - viewRow.FirstPrice) : '-' }}</span>
              </div>
            </el-col>
          </el-row>
        </div>
        <!-- 其他信息 -->
        <div class="view-section">
          <div class="view-section-title">其他信息</div>
          <el-row :gutter="getFormGutter()">
            <el-col :span="24">
              <div class="view-item">
                <span class="view-label">备注</span>
                <span class="view-value view-value-block">{{ viewRow.note || '-' }}</span>
              </div>
            </el-col>
            <el-col :span="24">
              <div class="view-item">
                <span class="view-label">抄送人</span>
                <div class="view-value view-value-block">
                  <div v-if="viewCcUsers.length" class="cc-chip-list">
                    <div v-for="u in viewCcUsers" :key="u.id" class="cc-chip">
                      <user-avatar
                        :name="u.name"
                        :username="u.username"
                        :avatar="u.avatar"
                        :user-id="u.id"
                        size="sm"
                      />
                      <span class="cc-chip-text">
                        {{ u.name }}
                        <span
                          v-if="u.departmentName"
                          class="cc-chip-dept"
                          :style="ccDeptStyle(u.departmentName)"
                        >{{ u.departmentName }}</span>
                      </span>
                    </div>
                  </div>
                  <span v-else>{{ viewRow.cc_names || '-' }}</span>
                </div>
              </div>
            </el-col>
            <el-col :span="24">
              <div class="view-item">
                <span class="view-label">合同附件</span>
                <span class="view-value" v-if="viewRow.attachment_name">
                  <span class="attachment-link" @click="downloadAttachment(viewRow)">📄 {{ viewRow.attachment_name }}</span>
                </span>
                <span class="view-value" v-else>无附件</span>
              </div>
            </el-col>
          </el-row>
        </div>
        <div class="dialog-footer">
          <el-button @click="ifShowViewDialog = false" size="small">关闭</el-button>
        </div>
      </div>
    </el-dialog>

    <!-- 编辑抄送人对话框（管理员 / 部门经理） -->
    <el-dialog
      title="编辑抄送人"
      :visible.sync="ifShowCcDialog"
      width="920px"
      top="6vh"
      :fullscreen="isMobile"
      custom-class="cc-edit-dialog"
      @closed="onCcDialogClosed"
    >
      <div v-if="ccForm.contractName" class="cc-dialog-contract">
        合同：{{ ccForm.contractName }}
      </div>
      <cc-user-panel
        v-model="ccForm.ccUserIds"
        :options="ccCandidates"
        :selected-details="ccSelectedDetails"
        :loading="ccLoading"
      />
      <div class="cc-dialog-tip">
        勾选需要抄送的人员；不同部门用不同颜色区分。被抄送的人可在合同列表中查看该合同。
      </div>
      <span slot="footer">
        <el-button @click="ifShowCcDialog = false">取消</el-button>
        <el-button type="primary" :loading="ccSaving" @click="saveCc">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { delwork, getAllWorkList, exportSubjectPDF1, updateWorkContent, initSubject, initServiceContent, completeWork, undoCompleteWork, exportContractXLS, checkContractApplyable, getContractApplyStatus, getCcCandidates, getContractCcList, updateContractCc } from "../api";
import { getBaseURL } from "../api/http";
import { mapGetters } from "vuex";
import CcUserSelect from "../components/CcUserSelect.vue";
import CcUserPanel from "../components/CcUserPanel.vue";
import UserAvatar from "../components/UserAvatar.vue";

export default {
  name: "work",
  components: { CcUserSelect, CcUserPanel, UserAvatar },
  data() {
    return {
      loading: false,
      loadingSubjects: false,
      loadingServiceContents: false,
      ifSmall: false,
      isMobile: false,
      isTablet: false,
      screenWidth: 0,
      tableHeight: 600,
      tableKey: Date.now(),
      paginationLayout: "prev, pager,next, jumper, ->, total, sizes",
      // 附件上传相关
      currentAttachment: null,      // 当前合同的附件信息
      updateAttachmentFiles: [],    // 编辑时选择的附件文件
      updateAttachmentFile: null,   // 编辑时选择的文件对象
      exportLoading: false, // 导出加载状态
      isApplying: false,  // 防止重复提交

      // 所有列配置 - 短列固定宽度，长列使用min-width可扩展
      allColumns: [
        { name: "预警状态", value: "warningStatus", width: "80", ifShow: true, sortable: false, align: "center" },
        { name: "截止时间", value: "DeadLine", width: "120", ifShow: true, align: "center" },
        { name: "备注", value: "note", minWidth: "120", ifShow: true, align: "center" },
        { name: "申请人", value: "create_user_name", minWidth: "100", ifShow: false, align: "center" },
        { name: "抄送人", value: "cc_names", minWidth: "120", ifShow: false, align: "center" },
        { name: "序号", value: "id", width: "80", ifShow: true, align: "center" },
        { name: "归档编号/报告号", value: "ReturnId", minWidth: "140", ifShow: true, align: "center" },
        { name: "项目名称", value: "WorkName", minWidth: "200", ifShow: true, align: "center" },
        { name: "服务内容", value: "content", minWidth: "180", ifShow: true, align: "center" },
        { name: "建设单位", value: "build_company", minWidth: "150", ifShow: true, align: "center" },
        { name: "承接单位", value: "SubjectName", width: "100", ifShow: true, align: "center" },
        { name: "中标/送审单位", value: "win_company", minWidth: "150", ifShow: true, align: "center" },
        { name: "招标控制价/审定价", value: "FirstPrice", width: "130", ifShow: true, align: "center" },
        { name: "中标价/送审价", value: "SecondPrice", width: "130", ifShow: true, align: "center" },
        { name: "核减额", value: "MinusPrice", width: "120", ifShow: true, align: "center" },
        { name: "评委费", value: "PingPrice", width: "120", ifShow: true, align: "center" },
        { name: "已开票金额", value: "PayPrice", width: "130", ifShow: true, align: "center" },
        { name: "申请状态", value: "applyStatus", width: "100", ifShow: true, sortable: false, align: "center" },
        { name: "创建时间", value: "createAt", width: "180", ifShow: false, align: "center" },
        { name: "更新时间", value: "updateAt", width: "180", ifShow: false, align: "center" },
        { name: "合同金额", value: "HtPrice", width: "120", ifShow: true, align: "center" },
        { name: "附件", value: "attachment_name", minWidth: "150", ifShow: true, align: "center" },
      ],

      // 移动端列配置 - 调整为更少的列
      mobileColumns: [
        { name: "预警", value: "warningStatus", width: "60", ifShow: true, align: "center" },
        { name: "截止时间", value: "DeadLine", width: "100", ifShow: true, align: "center" },
        { name: "备注", value: "note", minWidth: "120", ifShow: true, align: "center" },
        { name: "申请人", value: "create_user_name", minWidth: "100", ifShow: false, align: "center" },
        { name: "序号", value: "id", width: "80", ifShow: true, align: "center" },
        { name: "项目名称", value: "WorkName", minWidth: "120", ifShow: true, align: "center" },
        { name: "建设单位", value: "build_company", minWidth: "100", ifShow: true, align: "center" },
        { name: "承接单位", value: "SubjectName", width: "100", ifShow: false, align: "center" },
      ],

      // 平板端列配置
      tabletColumns: [
        { name: "预警状态", value: "warningStatus", width: "75", ifShow: true, align: "center" },
        { name: "截止时间", value: "DeadLine", width: "110", ifShow: true, align: "center" },
        { name: "备注", value: "note", minWidth: "120", ifShow: true, align: "center" },
        { name: "申请人", value: "create_user_name", minWidth: "100", ifShow: false, align: "center" },
        { name: "序号", value: "id", width: "80", ifShow: true, align: "center" },
        { name: "归档编号/报告号", value: "ReturnId", minWidth: "140", ifShow: true, align: "center" },
        { name: "项目名称", value: "WorkName", minWidth: "150", ifShow: true, align: "center" },
        { name: "建设单位", value: "build_company", minWidth: "120", ifShow: true, align: "center" },
        { name: "承接单位", value: "SubjectName", width: "100", ifShow: true, align: "center" },
        { name: "附件", value: "attachment_name", minWidth: "120", ifShow: true, align: "center" },
      ],

      currentPage: 1,
      pageSize: 10,
      tableData: [],
      dataCount: 0,
      orderMode: "",
      orderField: "",

      form2Query: {
        key: "",
      },
      /** 首页仪表盘跳转：与后端 contractFilter 一致 */
      contractFilter: "",

      subjectList: [],
      serviceContentList: [],
      newForm: {
        number: "",
        ReturnId: "",
        content: "",
        build_company: "",
        receive_companyId: "",
        win_company: "",
        DeadLine: "",
        WorkName: "",
        FirstPrice: null,
        SecondPrice: null,
        PingPrice: null,
        PayPrice: null,
        note: "",
        HtPrice: null,
        ccUserIds: [],
      },

      formRules: {
        WorkName: [
          { required: true, message: '请输入项目名称', trigger: 'blur' }
        ],
        DeadLine: [
          { required: true, message: '请选择截止时间', trigger: 'change' }
        ]
      },

      ifShowUpdateDialog: false,
      selecthetong: {},
      visibleColumns: ['warningStatus', 'DeadLine', 'WorkName', 'build_company', 'SecondPrice', 'attachment_name'],
      // 查看合同（只读）对话框
      ifShowViewDialog: false,
      viewRow: null,
      viewCcUsers: [],
      // 抄送人
      ccCandidates: [],
      ccLoading: false,
      ccSaving: false,
      ifShowCcDialog: false,
      ccForm: {
        contractId: null,
        contractName: '',
        ccUserIds: []
      },
      ccSelectedDetails: [],
    };
  },

  computed: {
    ...mapGetters(["name", "roleId"]),

    canManageCc() {
      return this.roleId == 1 || this.roleId == 4;
    },

    // 计算操作列宽度（恢复原来的宽度设置）
    getActionColumnWidth() {
      return () => {
        if (this.isMobile) {
          return this.canManageCc ? "230" : (this.roleId == 1 ? "200" : "190");
        } else if (this.isTablet) {
          return this.canManageCc ? "210" : "180";
        }
        return this.canManageCc ? "260" : (this.roleId == 1 ? "220" : "200");
      };
    },

    // 根据屏幕尺寸返回列配置（管理员/项目经理才包含“申请人”列）
    visibleColumnOptions() {
      let cols = this.isMobile ? this.mobileColumns : (this.isTablet ? this.tabletColumns : this.allColumns);
      const showApplicant = this.roleId === 1 || this.roleId === 4;
      if (!showApplicant) {
        cols = cols.filter(c => c.value !== "create_user_name" && c.value !== "cc_names");
      }
      return cols;
    },

    // 计算实际显示的列
    computedColumns() {
      return this.visibleColumnOptions.map(col => ({
        ...col,
        ifShow: this.visibleColumns.includes(col.value)
      }));
    },

    // 是否所有列都显示
    isAllColumnsVisible() {
      const visibleOptions = this.visibleColumnOptions;
      return visibleOptions.every(col => this.visibleColumns.includes(col.value));
    }
  },

  mounted() {
    this.checkScreenSize();
    window.addEventListener('resize', this.handleResize);

    // 初始化
    this.orderField = "";
    this.orderMode = "";

    console.log("组件初始化完成，开始查询数据");
    console.log("当前visibleColumns:", this.visibleColumns);

    this.applyListQueryFromRoute();
    this.onQuery();
    this.loadSubjects();
    this.loadServiceContents();
    if (this.canManageCc) {
      this.loadCcCandidates();
    }
    
    // 检查是否有highlightId和view参数，如果有则打开详情对话框
    this.$nextTick(() => {
      const highlightId = this.$route.query.highlightId;
      const view = this.$route.query.view;
      if (highlightId && view === 'detail') {
        // 等待数据加载完成后再打开对话框
        setTimeout(() => {
          this.openDetailDialog(parseInt(highlightId));
        }, 1000);
      }
    });
  },
  
  watch: {
    '$route.query': {
      handler(newQuery) {
        if (newQuery.highlightId && newQuery.view === 'detail') {
          setTimeout(() => {
            this.openDetailDialog(parseInt(newQuery.highlightId));
          }, 500);
        }
        const cf = newQuery.contractFilter;
        const next =
          cf &&
          ["completed", "incomplete", "overdue", "urgent", "in_progress"].includes(cf)
            ? cf
            : "";
        let needReload = false;
        if (next !== this.contractFilter) {
          this.contractFilter = next;
          needReload = true;
        }
        if (Object.prototype.hasOwnProperty.call(newQuery, "key")) {
          const k = newQuery.key != null ? String(newQuery.key) : "";
          if (k !== this.form2Query.key) {
            this.form2Query.key = k;
            needReload = true;
          }
        }
        if (needReload) {
          this.currentPage = 1;
          this.onQuery();
        }
      },
      deep: true,
    },
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },

  methods: {
    // 检查屏幕尺寸
    checkScreenSize() {
      this.screenWidth = document.documentElement.clientWidth;
      this.isMobile = this.screenWidth < 768;
      this.isTablet = this.screenWidth >= 768 && this.screenWidth < 1200;
      this.ifSmall = this.isMobile || this.isTablet;

      // 自适应表格高度
      this.tableHeight = this.isMobile ? 400 : this.isTablet ? 500 : 600;



      // 自适应布局配置
      if (this.isMobile) {
        this.paginationLayout = "prev, pager, next, ->, total";
        this.visibleColumns = ['warningStatus', 'DeadLine', 'id', 'WorkName', 'build_company'];
      } else if (this.isTablet) {
        this.paginationLayout = "prev, pager, next, total, sizes";
        this.visibleColumns = ['warningStatus', 'DeadLine', 'id', 'WorkName', 'build_company', 'SubjectName'];
      } else {
        this.paginationLayout = "prev, pager, next, jumper, ->, total, sizes";
        this.visibleColumns = ['warningStatus', 'DeadLine', 'id', 'WorkName', 'build_company', 'SubjectName', 'win_company'];
      }

      this.tableKey = Date.now();
    },

    handleResize() {
      this.checkScreenSize();
    },

    // 获取对话框宽度
    getDialogWidth() {
      if (this.isMobile) return '95%';
      if (this.isTablet) return '85%';
      if (this.screenWidth < 1400) return '90%';
      return '900px';
    },

    // 获取表单间距
    getFormGutter() {
      if (this.isMobile) return 8;
      if (this.isTablet) return 16;
      return 20;
    },

    // 获取最小列宽
    getMinWidth(field) {
      const minWidths = {
        warningStatus: 60,
        DeadLine: 100,
        WorkName: 120,
        build_company: 100,
        SubjectName: 70,
        SecondPrice: 90,
        attachment_name: 80,
        HtPrice: 90,
        ReturnId: 100,
        note: 80
      };
      return minWidths[field] || 80;
    },

    // 格式化单元格内容
    formatCellContent(value, field) {
      if (value === null || value === undefined || value === '') return '-';

      // 长文本截断
      if (typeof value === 'string' && value.length > 20 && !this.isMobile) {
        return value.substring(0, 20) + '...';
      }

      if (typeof value === 'string' && value.length > 10 && this.isMobile) {
        return value.substring(0, 10) + '...';
      }

      return value;
    },

    // 格式化附件名称
    formatAttachmentName(name) {
      if (!name) return '';
      if (name.length > (this.isMobile ? 6 : 12)) {
        return name.substring(0, this.isMobile ? 6 : 12) + '...';
      }
      return name;
    },

    // 加载承接单位列表
    async loadSubjects() {
      this.loadingSubjects = true;
      try {
        const res = await initSubject();
        if (res && res.msg) {
          try {
            let obj = JSON.parse(res.msg);
            if (obj.Subject && Array.isArray(obj.Subject)) {
              this.subjectList = obj.Subject;
            } else {
              this.subjectList = [];
            }
          } catch (e) {
            console.error('解析承接单位JSON失败:', e);
            this.subjectList = [];
          }
        }
      } catch (err) {
        console.error('加载承接单位失败:', err);
        this.subjectList = [];
      } finally {
        this.loadingSubjects = false;
      }
    },

    // 加载服务内容列表
    async loadServiceContents() {
      this.loadingServiceContents = true;
      try {
        const res = await initServiceContent();
        if (res && res.msg) {
          try {
            let obj = JSON.parse(res.msg);
            if (obj.ServiceContent && Array.isArray(obj.ServiceContent)) {
              this.serviceContentList = obj.ServiceContent;
            } else {
              this.serviceContentList = [];
            }
          } catch (e) {
            console.error('解析服务内容JSON失败:', e);
            this.serviceContentList = [];
          }
        }
      } catch (err) {
        console.error('加载服务内容失败:', err);
        this.serviceContentList = [];
      } finally {
        this.loadingServiceContents = false;
      }
    },

    // 在methods中添加，添加路由跳转（如果需要从合同列表直接查看申请详情）
    viewApplyHistory(row) {
      if (row.modify_lock === 1) {
        // 如果有锁定，查看相关申请
        this.$axios({
          url: '/yj/contractApply/getPendingApply',
          method: 'get',
          params: { contractId: row.id }
        }).then(res => {
          if (res.data.code === 0 && res.data.data) {
            this.$router.push({
              path: '/contract/apply/detail',
              query: { applyId: res.data.data.id }
            });
          }
        });
      }
    },

    // 格式化价格显示
    formatPrice(value) {
      if (value === null || value === undefined || value === '') return '-';
      return '¥' + Number(value).toFixed(2);
    },

    // 格式化日期显示
    formatDateForDisplay(dateStr) {
      if (!dateStr) return '-';
      return dateStr.substring(0, 10);
    },

    // 对话框关闭时清理
    handleDialogClosed() {
      this.updateAttachmentFiles = [];
      this.updateAttachmentFile = null;
      if (this.$refs.updateUpload) {
        this.$refs.updateUpload.clearFiles();
      }
    },

    applyListQueryFromRoute() {
      const cf = this.$route.query.contractFilter;
      this.contractFilter =
        cf &&
        ["completed", "incomplete", "overdue", "urgent", "in_progress"].includes(cf)
          ? cf
          : "";
      if (Object.prototype.hasOwnProperty.call(this.$route.query, "key")) {
        const k = this.$route.query.key;
        this.form2Query.key = k != null ? String(k) : "";
      }
    },

    resetQuery() {
      this.form2Query.key = '';
      this.currentPage = 1;
      this.orderField = "";
      this.orderMode = "";
      this.contractFilter = "";
      if (this.$route.query.contractFilter || this.$route.query.key !== undefined) {
        const q = { ...this.$route.query };
        delete q.contractFilter;
        delete q.key;
        this.$router.replace({ path: this.$route.path, query: q });
      }
      this.onQuery();
    },

    // 切换所有列的显示状态
    toggleAllColumns() {
      if (this.isAllColumnsVisible) {
        this.visibleColumns = [];
      } else {
        this.visibleColumns = this.visibleColumnOptions.map(col => col.value);
      }
    },

    // 导出Excel文件
    async onExportXLS() {
      if (this.tableData.length === 0) {
        this.$message.warning('没有数据可以导出');
        return;
      }

      this.exportLoading = true;

      try {
        // 构建导出参数
        const params = {
          key: this.form2Query.key || '',
          order: this.orderMode || '',
          field: this.orderField || ''
        };

        // 调用导出API
        const response = await exportContractXLS(params);

        if (!response.data) {
          throw new Error('返回数据为空');
        }

        // 创建下载链接
        const blob = new Blob([response.data], {
          type: 'application/vnd.ms-excel'
        });

        if (blob.size === 0) {
          throw new Error('文件内容为空');
        }

        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;

        // 生成默认文件名（包含小时分钟秒，与后端格式一致）
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        const seconds = String(now.getSeconds()).padStart(2, '0');
        const defaultFilename = `合同总表_${year}_${month}_${day}_${hours}_${minutes}_${seconds}.xls`;
        
        // 尝试从响应头获取文件名（与附件下载方式一致）
        let filename = defaultFilename;
        
        // 尝试从响应头获取文件名（axios可能将header名称转为小写）
        const contentDisposition = response.headers && (
          response.headers['content-disposition'] || 
          response.headers['Content-Disposition'] ||
          ''
        );
        
        if (contentDisposition) {
          // 优先尝试解析RFC 5987格式：filename*=UTF-8''编码后的文件名
          // RFC 5987格式：filename*=charset'lang'encoded-filename
          // 例如：filename*=UTF-8''%E5%90%88%E5%90%8C%E6%80%BB%E8%A1%A8.xls
          const rfc5987Match = contentDisposition.match(/filename\*=UTF-8''([^;\s]+)/i);
          if (rfc5987Match && rfc5987Match[1]) {
            try {
              // RFC 5987格式中，文件名是URL编码的，需要解码
              const encodedFilename = rfc5987Match[1].trim();
              const decoded = decodeURIComponent(encodedFilename);
              if (decoded && decoded.length > 0) {
                filename = decoded;
              }
            } catch (e) {
              // 解析失败，继续尝试其他格式
            }
          }
          
          // 如果RFC 5987解析失败，尝试标准格式：filename="文件名" 或 filename=文件名
          if (filename === defaultFilename) {
            // 匹配 filename="xxx" 或 filename=xxx 格式
            const matches = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n\s]+)/i);
            if (matches && matches[1]) {
              let extractedFilename = matches[1].replace(/^['"]|['"]$/g, '').trim();
              // 移除可能的转义字符
              extractedFilename = extractedFilename.replace(/\\"/g, '"');
              
              // 如果文件名被URL编码了，尝试解码
              try {
                // 先尝试直接解码
                const decoded = decodeURIComponent(extractedFilename);
                if (decoded && decoded.length > 0) {
                  filename = decoded;
                }
              } catch (e) {
                // 如果解码失败，直接使用原始值（可能已经是正确的文件名）
                if (extractedFilename && extractedFilename.length > 0) {
                  filename = extractedFilename;
                }
              }
            }
          }
        }

        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // 延迟释放URL
        setTimeout(() => {
          window.URL.revokeObjectURL(downloadUrl);
        }, 100);

        this.$message.success('导出成功');

      } catch (error) {

        // 尝试从错误响应中获取更多信息
        if (error.response) {
          const responseData = error.response.data;
          if (responseData) {
            // 如果已经是解析好的JSON对象
            if (typeof responseData === 'object' && responseData.msg) {
              this.$message.error(responseData.msg || '导出失败');
            } else if (typeof responseData === 'string') {
              // 如果是字符串，尝试解析
              try {
                const errorData = JSON.parse(responseData);
                this.$message.error(errorData.msg || '导出失败');
              } catch (e) {
                // 如果解析失败，尝试使用TextDecoder
                try {
                  const errorData = JSON.parse(new TextDecoder().decode(responseData));
                  this.$message.error(errorData.msg || '导出失败');
                } catch (e2) {
                  this.$message.error('导出失败，请重试');
                }
              }
            } else {
              // 其他情况
              this.$message.error('导出失败，请重试');
            }
          } else {
            this.$message.error(error.response.statusText || '导出失败，请重试');
          }
        } else {
          this.$message.error(error.message || '导出失败，请重试');
        }
      } finally {
        this.exportLoading = false;
      }
    },

    // 申请修改合同
    async handleApplyModify(row) {
  // 防重复提交
  if (this.isApplying) {
    console.log('正在处理中，请勿重复点击');
    return;
  }
  
  this.isApplying = true;
  console.log('申请修改合同:', row.id);

  try {
    // 先检查是否可以申请
    const checkRes = await checkContractApplyable({ contractId: row.id });
    console.log('检查申请状态结果:', checkRes);

    if (checkRes.code === 0) {
      // 保存合同数据到sessionStorage
      sessionStorage.setItem('currentContract', JSON.stringify(row));
      sessionStorage.setItem('contractId', row.id);
      
      // 跳转到申请页面，同时传递合同数据
      this.$router.push({
        path: '/yj/contract/apply',
        query: { 
          contractId: row.id,
          // 也可以把关键数据放在query中
          workName: encodeURIComponent(row.WorkName || ''),
          returnId: encodeURIComponent(row.ReturnId || ''),
          company: encodeURIComponent(row.build_company || '')
        }
      });
    } else {
      this.$message.warning(checkRes.msg || '无法申请修改');

      // 如果有待审核的申请，可以查看
      if (checkRes.data && checkRes.data.applyId) {
        this.$confirm('该合同已有待审核的申请，是否查看详情？', '提示', {
          confirmButtonText: '查看详情',
          cancelButtonText: '取消',
          type: 'warning'
        }).then(() => {
          this.$router.push({
            path: '/yj/contract/apply/detail',
            query: { applyId: checkRes.data.applyId }
          });
        }).catch(() => { });
      }
    }
  } catch (error) {
    console.error('检查申请状态失败:', error);
    this.$message.error('检查申请状态失败');
  } finally {
    // 重置状态
    setTimeout(() => {
      this.isApplying = false;
    }, 1000);
  }
},

    // 获取申请状态文本
    getApplyStatusText(row) {
      // 这里需要从row中获取申请状态信息
      // 你可以从后端查询该合同的申请状态，或者在onQuery时加载
      if (row.applyStatus) {
        switch (row.applyStatus) {
          case 0: return '待审核';
          case 1: return '已通过';
          case 2: return '已拒绝';
          case 3: return '已生效';
          default: return '正常';
        }
      }
      return row.modify_lock === 1 ? '处理中' : '正常';
    },

    // 获取申请状态标签类型
    getApplyStatusTagType(row) {
      if (row.applyStatus) {
        switch (row.applyStatus) {
          case 0: return 'warning';  // 待审核
          case 1: return 'success';  // 已通过
          case 2: return 'danger';   // 已拒绝
          case 3: return 'info';     // 已生效
          default: return '';
        }
      }
      return row.modify_lock === 1 ? 'warning' : '';
    },

    handleSizeChange(val) {
      this.pageSize = val;
      this.onQuery();
    },

    handleCurrentChange(val) {
      this.currentPage = val;
      this.onQuery();
    },

    // 打开查看合同对话框（员工/部门经理用，只读）
    handleView(row) {
      this.viewRow = row;
      this.viewCcUsers = [];
      this.ifShowViewDialog = true;
      if (row && row.id) {
        getContractCcList({ contractId: row.id })
          .then((res) => {
            if (res.code === 0) {
              this.viewCcUsers = res.data || [];
            }
          })
          .catch(() => {});
      }
    },

    loadCcCandidates() {
      this.ccLoading = true;
      getCcCandidates()
        .then((res) => {
          if (res.code === 0) {
            this.ccCandidates = (res.data || []).map((u) => ({
              ...u,
              id: Number(u.id),
            })).filter((u) => !isNaN(u.id));
          }
        })
        .catch(() => {})
        .finally(() => {
          this.ccLoading = false;
        });
    },

    normalizeCcIds(raw) {
      if (!raw) return [];
      if (Array.isArray(raw)) {
        return raw.map(id => Number(id)).filter(id => !isNaN(id));
      }
      return [];
    },

    ccDeptStyle(deptName) {
      const palette = [
        { color: "#1d6fd8", bg: "#e8f1fc" },
        { color: "#0d9488", bg: "#e6f7f5" },
        { color: "#c2410c", bg: "#fff1e8" },
        { color: "#7c3aed", bg: "#f3edff" },
        { color: "#b45309", bg: "#fff7e6" },
        { color: "#be123c", bg: "#ffe8ee" },
        { color: "#0369a1", bg: "#e6f4fb" },
        { color: "#15803d", bg: "#e8f8ee" },
        { color: "#a16207", bg: "#faf3e0" },
        { color: "#475569", bg: "#eef1f5" },
      ];
      const str = String(deptName || "");
      let h = 0;
      for (let i = 0; i < str.length; i++) {
        h = (h * 31 + str.charCodeAt(i)) >>> 0;
      }
      const p = palette[h % palette.length];
      return { color: p.color, backgroundColor: p.bg, borderColor: p.color };
    },

    openCcDialog(row) {
      if (!this.canManageCc) {
        this.$message.warning('您没有权限编辑抄送人');
        return;
      }
      this.ccForm.contractId = row.id;
      this.ccForm.contractName = row.WorkName || '';
      this.ccForm.ccUserIds = this.normalizeCcIds(row.cc_user_ids);
      this.ccSelectedDetails = [];
      this.loadCcCandidates();
      // 再拉一次最新抄送人，避免列表缓存不准
      getContractCcList({ contractId: row.id }).then((res) => {
        if (res.code === 0 && Array.isArray(res.data)) {
          this.ccSelectedDetails = res.data.map((u) => ({
            ...u,
            id: Number(u.id),
          })).filter((u) => !isNaN(u.id));
          this.ccForm.ccUserIds = this.ccSelectedDetails.map((u) => u.id);
        }
      }).catch(() => {});
      this.ifShowCcDialog = true;
    },

    onCcDialogClosed() {
      this.ccForm.ccUserIds = [];
      this.ccSelectedDetails = [];
    },

    saveCc() {
      if (!this.ccForm.contractId) return;
      this.ccSaving = true;
      const params = new URLSearchParams();
      params.append('contractId', this.ccForm.contractId);
      params.append('ccUserIds', (this.ccForm.ccUserIds || []).join(','));
      updateContractCc(params)
        .then((res) => {
          if (res.code === 0) {
            this.$message.success(res.msg || '抄送人已更新');
            this.ifShowCcDialog = false;
            this.onQuery();
          } else {
            this.$message.error(res.msg || '更新失败');
          }
        })
        .catch(() => {
          this.$message.error('更新失败，请重试');
        })
        .finally(() => {
          this.ccSaving = false;
        });
    },

    // 打开详情对话框（根据ID）
    openDetailDialog(contractId) {
      console.log('尝试打开合同详情对话框，合同ID:', contractId);
      // 从tableData中查找对应的合同
      const contract = this.tableData.find(item => item.id == contractId || item.id === contractId);
      if (contract) {
        console.log('在当前页找到合同，打开对话框');
        this.handleUpdate(null, contract);
      } else {
        // 如果当前页没有，尝试重新加载数据
        console.log('当前页未找到合同，尝试重新加载...');
        // 先尝试跳转到第一页
        this.currentPage = 1;
        this.onQuery().then(() => {
          setTimeout(() => {
            const contract = this.tableData.find(item => item.id == contractId || item.id === contractId);
            if (contract) {
              console.log('重新加载后找到合同，打开对话框');
              this.handleUpdate(null, contract);
            } else {
              console.warn('重新加载后仍未找到合同，ID:', contractId);
              // 尝试在所有页面中搜索
              this.searchContractAcrossPages(contractId);
            }
          }, 500);
        }).catch(() => {
          this.$message.warning('加载合同数据失败');
        });
      }
    },
    
    // 跨页搜索合同
    async searchContractAcrossPages(contractId) {
      let page = 1;
      const limit = 50;
      let found = false;
      
      while (page <= 10 && !found) {
        try {
          const params = new URLSearchParams();
          params.append("limit", limit);
          params.append("page", page);
          
          const res = await getAllWorkList(params);
          if (res.code === 0 && res.data) {
            const contract = res.data.find(item => item.id == contractId || item.id === contractId);
            if (contract) {
              found = true;
              // 跳转到对应页面
              this.currentPage = page;
              await this.onQuery();
              setTimeout(() => {
                this.handleUpdate(null, contract);
              }, 300);
              return;
            }
            
            if (res.data.length < limit) {
              break; // 已经是最后一页
            }
          }
          page++;
        } catch (error) {
          console.error('搜索合同失败:', error);
          break;
        }
      }
      
      if (!found) {
        this.$message.warning('未找到指定的合同');
      }
    },
    
    handleUpdate(index, row) {
      if (this.roleId != 1) {
        this.$message.warning('您没有权限编辑合同');
        return;
      }
      this.selecthetong = row;
      // 将 receive_companyId 转换为数字类型，确保与 el-select 的选项值类型匹配
      let receiveCompanyId = row.receive_companyId;
      if (receiveCompanyId) {
        // 如果是字符串，转换为数字；如果已经是数字，保持不变
        receiveCompanyId = typeof receiveCompanyId === 'string' ? parseInt(receiveCompanyId, 10) : receiveCompanyId;
        // 如果转换失败（NaN），设置为空字符串
        if (isNaN(receiveCompanyId)) {
          receiveCompanyId = '';
        }
      } else {
        receiveCompanyId = '';
      }
      
      this.newForm = {
        number: row.number || '',
        ReturnId: row.ReturnId || '',
        content: row.content || '',
        build_company: row.build_company || '',
        receive_companyId: receiveCompanyId,
        win_company: row.win_company || '',
        DeadLine: row.DeadLine ? row.DeadLine.substring(0, 10) : '',
        WorkName: row.WorkName || '',
        FirstPrice: row.FirstPrice,
        SecondPrice: row.SecondPrice,
        PingPrice: row.PingPrice,
        PayPrice: row.PayPrice,
        note: row.note || '',
        HtPrice: row.HtPrice,
        ccUserIds: this.normalizeCcIds(row.cc_user_ids),
      };

      // 设置当前附件信息
      if (row.attachment_name) {
        this.currentAttachment = {
          name: row.attachment_name,
          path: row.attachment_path || row.attachmentPath,
          contractId: row.id
        };
      } else {
        this.currentAttachment = null;
      }

      // 清空上传文件列表
      this.updateAttachmentFiles = [];
      this.updateAttachmentFile = null;
      if (this.$refs.updateUpload) {
        this.$refs.updateUpload.clearFiles();
      }

      // 确保承接单位列表已加载（如果列表为空，重新加载）
      if (!this.subjectList || this.subjectList.length === 0) {
        this.loadSubjects();
      }
      if (!this.serviceContentList || this.serviceContentList.length === 0) {
        this.loadServiceContents();
      }
      if (!this.ccCandidates.length) {
        this.loadCcCandidates();
      }
      getContractCcList({ contractId: row.id }).then((res) => {
        if (res.code === 0 && Array.isArray(res.data)) {
          this.newForm.ccUserIds = res.data.map(u => Number(u.id)).filter(id => !isNaN(id));
        }
      }).catch(() => {});

      this.ifShowUpdateDialog = true;
    },

    // 删除当前附件
    deleteCurrentAttachment() {
      this.currentAttachment = null;
    },

    // 编辑时的附件上传处理方法
    handleUpdateAttachmentChange(file, fileList) {
      if (fileList.length > 1) {
        fileList.splice(0, 1);
      }
      this.updateAttachmentFile = file;
      this.updateAttachmentFiles = fileList;

      // 更新当前附件显示（但未保存）
      this.currentAttachment = {
        name: file.name,
        file: file.raw
      };
    },

    handleUpdateAttachmentRemove() {
      this.updateAttachmentFile = null;
      this.updateAttachmentFiles = [];

      // 恢复原来的附件信息（如果有的话）
      if (this.selecthetong && this.selecthetong.attachment_name) {
        this.currentAttachment = {
          name: this.selecthetong.attachment_name,
          path: this.selecthetong.attachment_path || this.selecthetong.attachmentPath,
          contractId: this.selecthetong.id
        };
      } else {
        this.currentAttachment = null;
      }
    },

    beforeUpdateAttachmentUpload(file) {
      const isPDF = file.type === 'application/pdf';
      const isLt30M = file.size / 1024 / 1024 < 30;

      if (!isPDF) {
        this.$message.error('只能上传PDF格式的文件!');
        return false;
      }
      if (!isLt30M) {
        this.$message.error('文件大小不能超过30MB!');
        return false;
      }
      return true;
    },

    // 提交更新
    confirmUpdate() {
      this.$refs.updateForm.validate((valid) => {
        if (!valid) {
          this.$message.error('请填写完整信息');
          return;
        }

        let _this = this;

        // 使用 FormData
        const formData = new FormData();

        formData.append("id", this.selecthetong.id);
        formData.append("number", this.newForm.number);

        if (this.newForm.ReturnId !== undefined) {
          formData.append("ReturnId", this.newForm.ReturnId);
        }

        formData.append("content", this.newForm.content);
        formData.append("build_company", this.newForm.build_company);
        formData.append("receive_companyId", this.newForm.receive_companyId);
        formData.append("win_company", this.newForm.win_company || "");
        formData.append("DeadLine", this.newForm.DeadLine);
        formData.append("WorkName", this.newForm.WorkName);

        // 处理价格字段
        if (this.newForm.FirstPrice !== null && this.newForm.FirstPrice !== '' && this.newForm.FirstPrice !== undefined) {
          formData.append("FirstPrice", this.newForm.FirstPrice);
        }
        if (this.newForm.SecondPrice !== null && this.newForm.SecondPrice !== '' && this.newForm.SecondPrice !== undefined) {
          formData.append("SecondPrice", this.newForm.SecondPrice);
        }
        if (this.newForm.PingPrice !== null && this.newForm.PingPrice !== '' && this.newForm.PingPrice !== undefined) {
          formData.append("PingPrice", this.newForm.PingPrice);
        }
        if (this.newForm.PayPrice !== null && this.newForm.PayPrice !== '' && this.newForm.PayPrice !== undefined) {
          formData.append("PayPrice", this.newForm.PayPrice);
        }
        if (this.newForm.note !== undefined) {
          formData.append("note", this.newForm.note);
        }
        if (this.newForm.HtPrice !== null && this.newForm.HtPrice !== '' &&
          this.newForm.HtPrice !== undefined) {
          formData.append("HtPrice", this.newForm.HtPrice);
        }
        // 抄送人（始终提交，允许清空）
        formData.append("ccUserIds", (this.newForm.ccUserIds || []).join(","));

        // 如果有新附件，添加到 FormData
        if (this.updateAttachmentFile) {
          const file = this.updateAttachmentFile.raw || this.updateAttachmentFile;
          formData.append("file", file);
        }

        // 直接使用 updateWorkContent
        updateWorkContent(formData)
          .then((res) => {
            if (res.code === 0) {
              _this.$message.success(res.msg || '更新成功');
              _this.onQuery();
              _this.ifShowUpdateDialog = false;
              // 清空上传的文件
              _this.updateAttachmentFile = null;
              _this.updateAttachmentFiles = [];
              if (_this.$refs.updateUpload) {
                _this.$refs.updateUpload.clearFiles();
              }
            } else {
              _this.$message.error(res.msg || '更新失败');
            }
            _this.resetForm();
          })
          .catch((error) => {
            console.error('更新失败:', error);
            _this.$message.error('更新失败，请重试');
          });
      });
    },

    // 附件处理方法（上传或下载）
    handleAttachment(row) {
      if (this.roleId != 1) {
        this.$message.warning('您没有权限操作附件');
        return;
      }

      if (row.attachment_name) {
        this.onExportFile(null, row);
      } else {
        this.handleUpdate(null, row);
      }
    },

    resetForm() {
      this.newForm = {
        number: "",
        ReturnId: "",
        content: "",
        build_company: "",
        receive_companyId: "",
        win_company: "",
        DeadLine: "",
        WorkName: "",
        FirstPrice: null,
        SecondPrice: null,
        PingPrice: null,
        PayPrice: null,
        note: "",
        HtPrice: null,
        ccUserIds: [],
      };
    },

    handleDel(index, row) {
      if (this.roleId != 1) {
        this.$message.warning('您没有权限删除合同');
        return;
      }
      let params = new URLSearchParams();
      params.append("ids[]", row.id);
      let _this = this;

      this.$confirm('确定要删除这条合同记录吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        delwork(params)
          .then((res) => {
            if (res.code === 0) {
              _this.$message({
                message: res.msg,
                type: "success",
              });
              _this.onQuery();
            } else {
              _this.$message({
                message: res.msg,
                type: "error",
              });
            }
          })
          .catch(() => {
            _this.$message({
              message: "删除失败，请重试",
              type: "error",
            });
          });
      });
    },

    // 确认完成方法
    async confirmComplete(row) {
      try {
        await this.$confirm('确认该合同已完成所有工作？', '确认完成', {
          confirmButtonText: '确认完成',
          cancelButtonText: '取消',
          type: 'warning'
        });

        let params = new URLSearchParams();
        params.append("id", row.id);

        const res = await completeWork(params);

        if (res.code === 0) {
          this.$message.success(res.msg || '已标记为完成');
          this.onQuery();
        } else {
          this.$message.error(res.msg || '操作失败');
        }
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error('操作失败');
        }
      }
    },

    // 撤销完成状态
    async undoComplete(row) {
      try {
        await this.$confirm('确认撤销完成状态？合同将重新进入待办列表', '提示', {
          confirmButtonText: '确认撤销',
          cancelButtonText: '取消',
          type: 'warning'
        });

        let params = new URLSearchParams();
        params.append("id", row.id);

        const res = await undoCompleteWork(params);

        if (res.code === 0) {
          this.$message.success(res.msg || '已撤销完成状态');
          this.onQuery();
        } else {
          this.$message.error(res.msg || '操作失败');
        }
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error('操作失败');
        }
      }
    },

    onQuery() {
      this.loading = true;
      let params = new URLSearchParams();
      params.append("limit", this.pageSize);
      params.append("page", this.currentPage);
      params.append("key", this.form2Query.key);
      if (this.contractFilter) {
        params.append("contractFilter", this.contractFilter);
      }

      // 只有当用户点击了排序时才传递排序参数
      if (this.orderField && this.orderField.trim() !== '' &&
        this.orderMode && this.orderMode.trim() !== '') {
        params.append("order", this.orderMode);
        params.append("field", this.orderField);
      }

      return getAllWorkList(params)
        .then((res) => {
          // 修改后的状态计算
          this.tableData = res.data.map(item => {
            // 计算预警状态
            let warningColor = 'success';
            let warningText = '正常';
            let remainingDays = 0;

            // 重要：后端返回的是 is_completed（带下划线）
            const isCompleted = item.is_completed || item.isCompleted;

            if (item.DeadLine) {
              const deadline = new Date(item.DeadLine);
              const today = new Date();
              const diffTime = deadline.getTime() - today.getTime();
              remainingDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

              if (isCompleted) {
                // 已完成 - 绿色
                warningColor = 'success';
                warningText = '已完成';
              } else if (remainingDays < 0) {
                // 未完成 - 红色（紧急）
                warningColor = 'danger';
                warningText = '未完成';
              } else if (remainingDays <= 5) {
                // 5天内到期 - 红色
                warningColor = 'danger';
                warningText = '紧急';
              } else if (remainingDays <= 10) {
                // 10天内到期 - 橙色
                warningColor = 'warning';
                warningText = '预警';
              } else {
                // 正常 - 蓝色
                warningColor = 'info';
                warningText = '正常';
              }
              // 添加申请状态信息
              const applyStatusInfo = this.getApplyStatusInfo(item);
            }

            return {
              ...item,
              warningColor,
              warningText,
              remainingDays: isCompleted ? -999 : remainingDays,
              isCompleted: isCompleted, // 添加这个字段，确保前端能识别
              MinusPrice: item.FirstPrice && item.SecondPrice ? item.SecondPrice - item.FirstPrice : null,
              // 确保附件信息能正确获取
              attachment_name: item.attachment_name || item.attachmentName,
              attachment_path: item.attachment_path || item.attachmentPath
            };
          });

          this.dataCount = res.count;
          this.loading = false;
          return res;
        })
        .catch((error) => {
          console.error("加载数据失败:", error);
          this.loading = false;
          this.$message({
            message: "加载数据失败",
            type: "error",
          });
          throw error;
        });
    },

    // 获取申请状态信息（这里可以调用后端API获取具体状态）
    getApplyStatusInfo(item) {
      // 简单判断，你可以调用API获取更详细的状态
      if (item.modify_lock === 1) {
        return { status: 0, text: '待审核' };
      }
      return { status: null, text: '正常' };
    },

    onSortChange(res) {
      if (res.order) {
        // 用户点击了排序
        this.orderMode = res.order === "descending" ? "desc" : "asc";
        this.orderField = res.prop;
      } else {
        // 用户取消了排序（再次点击已排序的列）
        this.orderMode = "";
        this.orderField = "";
      }

      // 重置到第一页
      this.currentPage = 1;
      this.onQuery();
    },

    // 附件下载：axios blob；若返回 JSON 错误则提示，不再盲目新开窗口
    doDownloadAttachment(row) {
      if (!row || !row.attachment_name) {
        this.$message.warning('该合同没有附件');
        return;
      }
      this.loading = true;
      const params = { id: row.id };
      exportSubjectPDF1(params)
        .then(async (res) => {
          const ct = ((res.headers && (res.headers['content-type'] || res.headers['Content-Type'])) || '').toLowerCase();
          const data = res.data;
          // 后端权限/缺文件时仍可能 HTTP 200 + JSON
          if (ct.indexOf('application/json') >= 0 || ct.indexOf('text/') >= 0) {
            const text = await (data instanceof Blob ? data.text() : Promise.resolve(String(data || '')));
            let msg = '下载失败';
            try {
              const obj = JSON.parse(text);
              msg = obj.msg || obj.message || msg;
            } catch (e) {
              if (text) msg = text.slice(0, 120);
            }
            this.$message.error(msg);
            return;
          }
          if (!(data instanceof Blob) || data.size < 100) {
            // 过小的 blob 多半是错误 JSON
            try {
              const text = await data.text();
              const obj = JSON.parse(text);
              this.$message.error(obj.msg || '下载失败');
              return;
            } catch (e) {
              /* continue as file */
            }
          }
          const blob = new Blob([data], { type: 'application/pdf' });
          const href = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = href;
          let filename = row.attachment_name || '合同附件.pdf';
          if (filename.toLowerCase().endsWith('.zip')) {
            filename = filename.replace(/\.zip$/i, '.pdf');
          }
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(href);
          this.$message.success('下载成功');
        })
        .catch((err) => {
          console.error('下载失败:', err);
          const status = err && err.status;
          if (status === 502 || status === 504) {
            this.$message.error('服务器下载超时或网关错误，请稍后重试或联系管理员检查附件文件');
          } else if (status === 401 || status === 403) {
            this.$message.error('无权限下载，请重新登录后再试');
          } else {
            this.$message.error('下载失败，请稍后重试');
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },

    downloadAttachment(row) {
      this.doDownloadAttachment(row);
    },

    onExportFile(index, row) {
      this.doDownloadAttachment(row);
    },
  },
};
</script>

<style scoped>
.work {
  padding: 15px;
  background-color: #f5f7fa;
  min-height: calc(100vh - 60px);
}

/* 顶部标题+筛选区域卡片 */
.work-header-card {
  background: #ffffff;
  padding: 12px 15px 15px 15px;
  border-radius: 8px;
  margin-bottom: 15px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
}

.work-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.work-header-row h3 {
  margin: 0;
  color: #303133;
  font-weight: 600;
  font-size: 20px;
}

.work-header-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.work-search-input {
  width: 340px;
  max-width: 40vw;
}

/* 搜索区域 */
.demo-form-inline {
  padding: 0;
}

/* 列选择区域 */
.check-group {
  background: white;
  padding: 12px 15px;
  border-radius: 8px;
  margin-bottom: 15px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.check-group>span {
  font-size: 14px;
  color: #606266;
  font-weight: 500;
  margin-right: 10px;
  white-space: nowrap;
}

/* 表格容器 */
.table-container {
  background: white;
  padding: 15px;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
  overflow-x: auto;
}

/* 分页 */
.pagination {
  background: white;
  padding: 15px;
  border-radius: 8px;
  margin-top: 15px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
}

/* 对话框样式 */
.dialog-content {
  max-height: 70vh;
  overflow-y: auto;
  padding-right: 10px;
}

.dialog-header {
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e4e7ed;
  font-size: 14px;
  color: #606266;
}

.contract-name {
  color: #409eff;
  font-weight: 600;
}

.dialog-footer {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #e4e7ed;
  text-align: right;
}

/* 移动端适配 */
@media screen and (max-width: 768px) {
  .work {
    padding: 10px 8px;
  }

  .work-header-card {
    padding: 10px;
  }

  .work-header-row h3 {
    font-size: 18px;
  }

  .work-header-actions {
    flex-wrap: wrap;
  }

  .work-search-input {
    width: 100%;
  }

  .check-group {
    padding: 10px;
    margin-bottom: 10px;
  }

  .check-group>span {
    font-size: 13px;
    margin-bottom: 8px;
    width: 100%;
  }

  .check-group .el-checkbox-group {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .table-container {
    padding: 8px;
    margin-bottom: 10px;
  }

  .pagination {
    padding: 10px;
    margin-top: 10px;
  }

  .el-form-item {
    margin-bottom: 12px !important;
  }

  .dialog-content {
    max-height: 80vh;
  }

  .action-buttons {
    display: flex;
    flex-wrap: nowrap;
    overflow-x: auto;
    padding-bottom: 5px;
  }

  .action-buttons .el-button {
    white-space: nowrap;
    flex-shrink: 0;
    padding: 0 4px;
    min-width: auto;
    font-size: 12px;
  }
}

/* 平板端适配 */
@media screen and (min-width: 769px) and (max-width: 1199px) {
  .work {
    padding: 12px;
  }

  .demo-form-inline {
    padding: 12px;
  }

  .check-group {
    padding: 10px 12px;
  }

  .table-container {
    padding: 12px;
  }

  .action-buttons .el-button span {
    display: inline;
  }
}

/* 表格样式优化 */
.contract-table {
  font-size: 13px;
}

.contract-table .cell {
  line-height: 1.4;
  padding: 8px 10px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* 优化列宽调整拖拽区域 */
.contract-table .el-table__header-wrapper .el-table__header th {
  position: relative;
  padding: 0;
  user-select: none;
}

.contract-table .el-table__header-wrapper .el-table__header th .cell {
  padding: 12px 10px;
  display: inline-block;
  width: 100%;
  box-sizing: border-box;
  position: relative;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* 排序图标样式优化 - 固定在右侧 */
.contract-table .el-table__header-wrapper .el-table__header th.is-sortable .cell {
  padding-right: 30px;
}

.contract-table .el-table__header-wrapper .el-table__header th .caret-wrapper {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 24px;
  cursor: pointer;
  z-index: 1;
}

.contract-table .el-table__header-wrapper .el-table__header th .caret-wrapper:hover {
  background-color: rgba(64, 158, 255, 0.1);
  border-radius: 2px;
}

/* 列宽调整拖拽条样式优化 - 更明显的拖拽区域 */
.contract-table .el-table__header-wrapper .el-table__header th {
  position: relative;
}

.contract-table .el-table__header-wrapper .el-table__header th::after {
  content: '';
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 10px;
  cursor: col-resize;
  z-index: 2;
  background: transparent;
}

.contract-table .el-table__header-wrapper .el-table__header th:hover::after {
  background: linear-gradient(to right, transparent 0%, rgba(64, 158, 255, 0.1) 50%, transparent 100%);
}

.contract-table .el-table__header-wrapper .el-table__header th.is-resizing::after {
  background: rgba(64, 158, 255, 0.2);
  border-right: 2px solid #409EFF;
}

/* 确保排序图标和拖拽条不冲突 */
.contract-table .el-table__header-wrapper .el-table__header th.is-sortable::after {
  right: 0;
  width: 8px;
}

.contract-table .el-table__header-wrapper .el-table__header th.is-sortable .caret-wrapper {
  right: 12px;
}

/* 优化表格列宽调整时的视觉效果 */
.contract-table .el-table__header-wrapper .el-table__header th.is-resizing {
  background-color: rgba(64, 158, 255, 0.05);
}

/* 确保单元格内容不会溢出 */
.contract-table .el-table__body-wrapper .el-table__body td .cell {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 操作列样式优化 */
.contract-table .action-column {
  background-color: #FAFAFA;
}

.contract-table .action-column .cell {
  padding: 8px 5px;
}

/* 优化列宽调整时的视觉反馈 */
.contract-table .el-table__header-wrapper .el-table__header th.is-resizing {
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

/* 优化表格滚动条 */
.contract-table .el-table__body-wrapper {
  scrollbar-width: thin;
  scrollbar-color: #c1c1c1 #f1f1f1;
}

.contract-table .el-table__body-wrapper::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.contract-table .el-table__body-wrapper::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.contract-table .el-table__body-wrapper::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.contract-table .el-table__body-wrapper::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* 确保表格在不同屏幕尺寸下都能正常显示 */
@media screen and (max-width: 768px) {
  .contract-table .el-table__header-wrapper .el-table__header th .caret-wrapper {
    right: 8px;
    width: 12px;
    height: 20px;
  }
  
  .contract-table .el-table__header-wrapper .el-table__header th.is-sortable .cell {
    padding-right: 25px;
  }
  
  .contract-table .el-table__header-wrapper .el-table__header th::after {
    width: 6px;
  }
}

/* 优化表格边框和间距 */
.contract-table th,
.contract-table td {
  border-right: 1px solid #EBEEF5;
}

.contract-table th:last-child,
.contract-table td:last-child {
  border-right: none;
}

/* 表头样式优化 */
.contract-table .el-table__header-wrapper {
  background-color: #FAFAFA;
}

.contract-table .el-table__header th {
  background-color: #FAFAFA;
  font-weight: 600;
  color: #303133;
  transition: background-color 0.2s;
}

.contract-table .el-table__header th:hover {
  background-color: #F5F7FA;
}

/* 表格行样式优化 */
.contract-table .el-table__body-wrapper .el-table__body tr {
  transition: background-color 0.2s;
}

.contract-table .el-table__body-wrapper .el-table__body tr:hover {
  background-color: #F5F7FA;
}

/* 优化表格整体外观 */
.contract-table {
  border-radius: 4px;
  overflow: hidden;
}

.contract-table .el-table__header,
.contract-table .el-table__body {
  border-collapse: separate;
  border-spacing: 0;
}

/* 确保固定列也能正常调整宽度 */
.contract-table .el-table__fixed,
.contract-table .el-table__fixed-right {
  box-shadow: none;
}

/* 优化表格容器 */
.table-container {
  background: #fff;
  border-radius: 4px;
  overflow: auto;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

/* 滚动条美化 */
.table-container::-webkit-scrollbar {
  height: 8px;
}

.table-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.table-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.table-container::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.dialog-content::-webkit-scrollbar {
  width: 6px;
}

.dialog-content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.dialog-content::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.dialog-content::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* 价格输入框适配 */
.price-input .el-input__inner {
  text-align: right;
  padding-right: 45px;
}

.price-input .el-input-group__append {
  padding: 0 8px;
  width: 32px;
  min-width: 32px;
}

/* 响应式列宽调整 */
@media screen and (max-width: 768px) {
  .el-table .cell {
    padding: 6px 8px;
    font-size: 12px;
  }

  .el-table__header .cell {
    font-size: 12px;
    padding: 8px 5px;
  }
}

@media screen and (min-width: 769px) and (max-width: 1199px) {
  .el-table .cell {
    font-size: 13px;
  }
}

/* 未完成的特殊样式 */
.el-table .warning-row--danger {
  background-color: #fff1f0 !important;
}

.el-table .warning-row--danger:hover>td {
  background-color: #ffccc7 !important;
}

/* 已完成的样式 */
.el-table .warning-row--success {
  background-color: #f6ffed !important;
  opacity: 0.8;
}

.el-table .warning-row--success:hover>td {
  background-color: #d9f7be !important;
}

/* 操作按钮样式 */
.action-buttons .el-button {
  transition: all 0.3s;
}

.action-buttons .el-button:hover {
  transform: translateY(-1px);
}

/* 对话框内容 */
.dialog-content {
  max-height: 75vh;
  overflow-y: auto;
  padding-right: 10px;
}

.dialog-header {
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e4e7ed;
  font-size: 16px;
  color: #303133;
  font-weight: 600;
}

.contract-name {
  color: #409eff;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
}

/* 表单分区 */
.form-section {
  margin-bottom: 25px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e4e7ed;
}

.section-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 15px;
  padding-left: 8px;
  border-left: 4px solid #409eff;
  line-height: 1;
}

/* 查看合同对话框（只读） */
.view-dialog .el-dialog__body {
  padding: 15px 20px 20px;
}
.view-dialog-content {
  max-height: 75vh;
  overflow-y: auto;
  padding-right: 8px;
}
.view-dialog-header {
  margin-bottom: 18px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e4e7ed;
  font-size: 16px;
  color: #303133;
  font-weight: 600;
}
.view-section {
  margin-bottom: 22px;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e4e7ed;
}
.view-section-title {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 12px;
  padding-left: 8px;
  border-left: 4px solid #909399;
  line-height: 1;
}
.view-item {
  margin-bottom: 12px;
}
.view-item:last-child {
  margin-bottom: 0;
}
.view-item-block {
  margin-bottom: 0;
}
.view-label {
  display: block;
  font-size: 12px;
  color: #909399;
  margin-bottom: 4px;
}
.view-value {
  font-size: 14px;
  color: #303133;
  word-break: break-all;
}
.view-value-block {
  display: block;
  white-space: pre-wrap;
  line-height: 1.5;
}
.view-dialog .attachment-link {
  color: #409eff;
  cursor: pointer;
}
.view-dialog .attachment-link:hover {
  text-decoration: underline;
}
.cc-chip-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.cc-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px 4px 4px;
  background: #f5f7fa;
  border-radius: 16px;
  border: 1px solid #e4e7ed;
}
.cc-chip-text {
  font-size: 13px;
  color: #303133;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.cc-chip-dept {
  font-size: 12px;
  border-radius: 3px;
  padding: 0 6px;
  line-height: 18px;
  border: 1px solid transparent;
}

/* 表单项目 */
.el-form-item {
  margin-bottom: 15px;
}

::v-deep .el-form-item__label {
  font-weight: 500;
  color: #606266;
  margin-bottom: 5px;
  display: block;
}

.form-item {
  width: 100%;
}

/* 附件区域 */
.attachment-section {
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  padding: 15px;
  background: white;
}

.current-attachment {
  margin-bottom: 15px;
}

.attachment-info {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  background: #f5f7fa;
  border-radius: 4px;
  border: 1px solid #ebeef5;
}

.attachment-icon {
  font-size: 16px;
  margin-right: 8px;
  flex-shrink: 0;
}

.attachment-name {
  flex: 1;
  font-size: 13px;
  color: #409eff;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.delete-btn {
  margin-left: 10px;
  color: #f56c6c;
  flex-shrink: 0;
}

.upload-area {
  text-align: center;
}

.upload-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 8px;
  line-height: 1.4;
}

/* 对话框底部按钮 */
.dialog-footer {
  margin-top: 25px;
  padding-top: 20px;
  border-top: 1px solid #e4e7ed;
  text-align: right;
  display: flex;
  gap: 10px;
}

.dialog-footer .el-button {
  flex: 1;
  min-width: 100px;
}

/* 移动端适配 */
@media screen and (max-width: 768px) {
  .dialog-content {
    max-height: 80vh;
    padding: 0 5px;
  }

  .form-section {
    padding: 15px;
    margin-bottom: 15px;
  }

  .section-title {
    font-size: 13px;
    margin-bottom: 12px;
  }

  .el-form-item {
    margin-bottom: 10px !important;
  }

  ::v-deep .el-col {
    margin-bottom: 8px;
  }

  .dialog-footer {
    margin-top: 20px;
    padding-top: 15px;
    flex-direction: column;
  }

  .dialog-footer .el-button {
    width: 100%;
    margin: 0;
  }

  .attachment-info {
    flex-wrap: wrap;
    gap: 8px;
  }

  .attachment-name {
    flex-basis: 100%;
    order: 2;
  }

  .delete-btn {
    order: 3;
  }
}

/* 平板端适配 */
@media screen and (min-width: 769px) and (max-width: 1199px) {
  .form-section {
    padding: 15px;
  }

  .dialog-footer {
    flex-direction: row;
  }

  .dialog-footer .el-button {
    flex: none;
  }
}

/* 必填项标记 */
::v-deep .el-form-item.is-required .el-form-item__label:before {
  content: '*';
  color: #f56c6c;
  margin-right: 4px;
}

/* 表单响应式间距 */
.el-row {
  margin: 0 !important;
}

.el-col {
  padding: 0 !important;
}

/* 移动端表格操作按钮间距 */
@media screen and (max-width: 768px) {
  .action-buttons {
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
  }

  .action-buttons::-webkit-scrollbar {
    display: none;
  }
}

/* 申请状态标签样式 */
.apply-status-pending {
  background-color: #fdf6ec;
  border-color: #f5dab1;
  color: #e6a23c;
}

.apply-status-approved {
  background-color: #f0f9eb;
  border-color: #c2e7b0;
  color: #67c23a;
}

.apply-status-rejected {
  background-color: #fef0f0;
  border-color: #fbc4c4;
  color: #f56c6c;
}

.apply-status-effective {
  background-color: #ecf5ff;
  border-color: #b3d8ff;
  color: #409eff;
}
</style>

<style>
.cc-edit-dialog {
  max-width: 96vw;
}
.cc-edit-dialog .el-dialog__body {
  padding: 12px 24px 8px;
}
.cc-edit-dialog .cc-dialog-contract {
  margin-bottom: 12px;
  font-size: 14px;
  color: #606266;
}
.cc-edit-dialog .cc-dialog-tip {
  margin-top: 10px;
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
}
</style>
